import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output } from '@angular/core'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { OrganizationUser, USER_ROLES } from '@app/v2/shared/types/user.type'

export const TEAM_MEMBER_ROLE_UPDATE_MODAL_ID = 'team-member-role-update-modal'

@Component({
  selector: 'app-team-member-role-update-modal',
  imports: [DeleteModalComponent],
  standalone: true,
  templateUrl: './team-member-role-update-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamMemberRoleUpdateModalComponent implements OnInit {
  public user = input.required<OrganizationUser>()
  public role = input.required<USER_ROLES>()

  public confirmed = output<{ id: number; role: USER_ROLES }>()
  public closed = output<void>()

  public readonly modalId = TEAM_MEMBER_ROLE_UPDATE_MODAL_ID
  public readonly message = computed(() => {
    const name = this.user().fullname?.trim() || this.user().email
    const currentRole = this._formatRole(this.user().role)
    const nextRole = this._formatRole(this.role())
    return `Are you sure you want to change <strong>${name}</strong>'s role from <strong>${currentRole}</strong> to <strong>${nextRole}</strong>?`
  })

  private readonly _modalService = inject(ModalService)

  ngOnInit(): void {
    if (!this._modalService.isModalOpen(this.modalId)) this._modalService.toggleModal(this.modalId)
  }

  /**
   * @description Handles confirmation of team member role update
   * @returns {void}
   */
  public onConfirm(): void {
    this.confirmed.emit({ id: this.user().id, role: this.role() })
  }

  /**
   * @description Handles cancellation of team member role update
   * @returns {void}
   */
  public onCancel(): void {
    if (this._modalService.isModalOpen(this.modalId)) this._modalService.toggleModal(this.modalId)
    this.closed.emit()
  }

  /**
   * @description Formats the role name
   * @param {USER_ROLES} role Role
   * @returns {string} Formatted role name
   */
  private _formatRole(role: USER_ROLES): string {
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
  }
}
